export const DEVICE_IDENTIFIER_STORAGE_KEY = "device_identifier";

export interface WebDeviceSnapshot {
  identifier: string
  name: string
  type: "browser"
  brand: string
  model: string
}

export function getOrCreateDeviceIdentifier(storage: Storage = localStorage): string {
  const existing = storage.getItem(DEVICE_IDENTIFIER_STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const identifier = globalThis.crypto?.randomUUID?.() || Math.random().toString(36).substring(2);
  storage.setItem(DEVICE_IDENTIFIER_STORAGE_KEY, identifier);
  return identifier;
}

export function parseBrowserInfo(userAgent: string): { model: string, version: string, brand: string } {
  let model = "";
  let version = "";
  let brand = "";

  if (userAgent.includes("Edg")) {
    const match = userAgent.match(/Edg\/(\d+\.\d+)/);
    model = "Edge";
    brand = "Microsoft";
    version = match ? match[1] : "";
  } else if (userAgent.includes("Chrome")) {
    const match = userAgent.match(/Chrome\/(\d+\.\d+)/);
    model = "Chrome";
    brand = "Google";
    version = match ? match[1] : "";
  } else if (userAgent.includes("Firefox")) {
    const match = userAgent.match(/Firefox\/(\d+\.\d+)/);
    model = "Firefox";
    brand = "Mozilla";
    version = match ? match[1] : "";
  } else if (userAgent.includes("Safari")) {
    const match = userAgent.match(/Version\/(\d+\.\d+)/);
    model = "Safari";
    brand = "Apple";
    version = match ? match[1] : "";
  }

  return { model, version, brand };
}

export function parseOperatingSystem(userAgent: string): { name: string, version: string } {
  if (userAgent.includes("Windows")) {
    const windowsVersion = userAgent.match(/Windows NT (\d+\.\d+)/);
    return {
      name: "Windows",
      version: windowsVersion ? windowsVersion[1] : "",
    };
  }

  if (userAgent.includes("Mac")) {
    const macVersion = userAgent.match(/Mac OS X (\d+[._]\d+)/);
    return {
      name: "macOS",
      version: macVersion ? macVersion[1].replace("_", ".") : "",
    };
  }

  if (userAgent.includes("iPhone") || userAgent.includes("iPad") || userAgent.includes("iPod")) {
    const iosVersion = userAgent.match(/OS (\d+[._]\d+)/);
    return {
      name: "iOS",
      version: iosVersion ? iosVersion[1].replace("_", ".") : "",
    };
  }

  if (userAgent.includes("Android")) {
    const androidVersion = userAgent.match(/Android (\d+\.\d+)/);
    return {
      name: "Android",
      version: androidVersion ? androidVersion[1] : "",
    };
  }

  if (userAgent.includes("Linux")) {
    return {
      name: "Linux",
      version: "",
    };
  }

  return {
    name: "",
    version: "",
  };
}

export function computeWebDeviceSnapshot(input: {
  identifier: string
  userAgent?: string
} = { identifier: "" }): WebDeviceSnapshot {
  const userAgent = input.userAgent ?? (typeof navigator !== "undefined" ? navigator.userAgent : "");
  const browserInfo = parseBrowserInfo(userAgent);
  const os = parseOperatingSystem(userAgent);
  const name = `${browserInfo.model} ${browserInfo.version} on ${os.name}`.substring(0, 255).trim();

  return {
    identifier: input.identifier.substring(0, 128),
    name,
    type: "browser",
    brand: browserInfo.brand.substring(0, 32),
    model: browserInfo.model.substring(0, 128),
  };
}
